import { useState, useEffect, useRef } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { BRAND, BRAND_DARK, BRAND_LIGHT } from "@/shared/constants.js";
import { STEP_PATHS } from "@/modules/Quote/constants.js";
import { ZIPCODE_NOT_AVAILABLE_MSG, toError } from "@/shared/utils/misc.js";
import { isValidEmail, isValidPassword, isValidPhone, PASSWORD_RULE } from "@/shared/utils/validators.js";
import { loadGsiScript } from "@/modules/Auth/utils/google.js";

import { ilfDlfHasRequiredDefaults, softQuotePremiumFromIlf } from "@/modules/Quote/utils/ilfHelpers.js";
import {
  buildSubmissionRequest,
  googleDataFromResponse,
  submissionIdFromSignupResponse,
} from "@/modules/Quote/utils/submission.js";

import {
  fetchGoogleClientId,
  postInsuredSignup,
  postSignInWithGoogleData,
} from "@/modules/Auth/api/authApi.js";
import { getZipcodeData } from "@/modules/Quote/api/ratingApi.js";

import { Field } from "@/shared/components/Field.jsx";
import { TextInput } from "@/shared/components/TextInput.jsx";
import { QuoteStrip } from "@/modules/Quote/components/QuoteStrip.jsx";
import { Spacer } from "@/shared/components/Spacer.jsx";
import { EyeIcon, SaveIcon, UserIcon } from "@/shared/components/Icon.jsx";
import Alert from "@/shared/components/Alert.jsx";
import { btnPrimary, dis } from "@/shared/utils/styles.js";

import { useStore } from "@/shared/store/useStore.js";
import signupStore from "@/modules/Auth/store/signupStore.js";
import quoteFormStore from "@/modules/Quote/store/quoteFormStore.js";
import questionsStore from "@/modules/Quote/store/questionsStore.js";
import personalStore from "@/modules/Quote/store/personalStore.js";
import submissionStore from "@/modules/Quote/store/submissionStore.js";
import insuredProfileStore from "@/shared/store/insuredProfileStore.js";
import ilfDlfStore from "@/modules/Quote/store/ilfDlfStore.js";
import specialityStore from "@/modules/Quote/store/specialityStore.js";
import sessionStore from "@/shared/store/sessionStore.js";
import modalStore from "@/shared/store/modalStore.js";

import { useIlfDlfFetcher, useIlfDlfResponse } from "@/hooks/useIlfDlfFetcher.js";
import { useSpecialityId } from "@/hooks/useSpeciality.js";

export default function RegistrationPage() {
  const navigate = useNavigate();
  // Purely-local UI state — no other page reads it.
  const [showPw, setShowPw] = useState(false);

  // Reset every signup field whenever the user arrives at /register from
  // anywhere else. City/State are intentionally cleared too — they'll be
  // re-derived from the current quoteFormStore.zip by the lookup effect
  // below, so the address shown always matches the active quote ZIP, not
  // whatever the user typed on a previous visit. Mount-only; later edits
  // persist. Google's prefill path runs in click handlers *after* this.
  useEffect(() => {
    signupStore.regFirstName = "";
    signupStore.regLastName = "";
    signupStore.regEmail = "";
    signupStore.regPhone = "";
    signupStore.regPassword = "";
    signupStore.regStreet = "";
    signupStore.regCity = "";
    signupStore.regState = "";
    signupStore.registered = false;
    signupStore.signupLoading = false;
    signupStore.signupError = null;
    signupStore.signupDone = false;
    signupStore.needsSignupCompletion = false;
    signupStore.googleSignupLoading = false;
    signupStore.googleSignupError = null;
  }, []);

  // ── Store-backed reads ─────────────────────────────────────────────────
  const regFirstName       = useStore(signupStore, (s) => s.regFirstName);
  const regLastName        = useStore(signupStore, (s) => s.regLastName);
  const regEmail           = useStore(signupStore, (s) => s.regEmail);
  const regPhone           = useStore(signupStore, (s) => s.regPhone);
  const regPassword        = useStore(signupStore, (s) => s.regPassword);
  const regStreet          = useStore(signupStore, (s) => s.regStreet);
  const regCity            = useStore(signupStore, (s) => s.regCity);
  const regState           = useStore(signupStore, (s) => s.regState);
  const signupError        = useStore(signupStore, (s) => s.signupError);
  const signupLoading      = useStore(signupStore, (s) => s.signupLoading);
  const googleSignupLoading = useStore(signupStore, (s) => s.googleSignupLoading);
  const googleSignupError  = useStore(signupStore, (s) => s.googleSignupError);
  const needsSignupCompletion = useStore(signupStore, (s) => s.needsSignupCompletion);

  const zip     = useStore(quoteFormStore, (s) => s.zip);
  const effDate = useStore(quoteFormStore, (s) => s.effDate);

  const questionAnswers       = useStore(questionsStore, (s) => s.questionAnswers);
  const impactAnswers         = useStore(questionsStore, (s) => s.impactAnswers);
  const visitedQuestionGroups = useStore(questionsStore, (s) => s.visitedQuestionGroups);
  const questionGroupsWithIds = useStore(questionsStore, (s) => s.questionGroupsWithIds);
  // Master-tree hidden set — this page never mutates it, only forwards it into
  // its own buildSubmissionRequest call (anonymous-signup path, §6.9).
  const masterHiddenQuestionIds = useStore(questionsStore, (s) => s.masterHiddenQuestionIds);

  const insuredProfile  = useStore(insuredProfileStore, (s) => s.insuredProfile);
  const isAuthenticated = Boolean(insuredProfile?.id || insuredProfile?.name || insuredProfile?.username);

  const fetchIlfDlf    = useIlfDlfFetcher();
  const ilfDlfResponse = useIlfDlfResponse();
  const specialityId   = useSpecialityId();

  // ── Derived: estimated-quote panel (pre-submission, simple case) ─────
  // Pre-signup there's no paymentOrderDetails yet, so the panel reads
  // straight off the ILF/DLF response.
  const snapshotPremium = Number.isFinite(Number(ilfDlfResponse?.premium))
    ? Math.round(Number(ilfDlfResponse.premium))
    : 0;
  const snapshotLimits = ilfDlfResponse?.defaultIlfDlfName || "—";
  const snapshotPolicyLine = (() => {
    const defense = ilfDlfResponse?.defaultDefense;
    return defense ? `Claims-made · ${defense}` : "Claims-made";
  })();
  // `total` already includes premium + fees + taxes; fall back to the base
  // premium when the response has no total.
  const snapshotTotal = softQuotePremiumFromIlf(ilfDlfResponse) ?? snapshotPremium;
  const hasEstimate = ilfDlfHasRequiredDefaults(ilfDlfResponse) && snapshotTotal > 0;
  // Unrounded total for display — the strip shows cents (2dp), so it must
  // not reuse the rounded gating values above.
  const rawTotal = Number.isFinite(Number(ilfDlfResponse?.total))
    ? Number(ilfDlfResponse.total)
    : (Number.isFinite(Number(ilfDlfResponse?.premium)) ? Number(ilfDlfResponse.premium) : 0);

  // ── Per-step validation (Continue button gate) ─────────────────────────
  // Mirrors the spec rules (weborder.json `InsuredRequest`) so a failing
  // client check matches what the server's 400 would have said.
  const canAdvance = () =>
    regFirstName.trim().length > 1
    && regLastName.trim().length > 1
    && isValidEmail(regEmail)
    && isValidPhone(regPhone)
    && isValidPassword(regPassword)
    && regStreet.trim().length > 3;

  // ── Auto-fill city/state + refresh ILF/DLF when zip changes ───────────
  // Fires on initial mount AND whenever the user edits the zip on this
  // page. Both endpoints are deduped via their own refs so re-entering
  // the same 5-digit zip (or React Strict Mode's mount double-fire in
  // dev) won't re-trigger either lookup. Each ref is set *before* the
  // await so a second fire interleaved with the first won't slip
  // through; on failure we roll the ref back so a retry can hit the
  // API again instead of being silently deduped.
  const lastFetchedZipcodeApiRef = useRef(null);
  const lastFetchedIlfDlfApiRef = useRef(null);
  useEffect(() => {
    if (zip.length !== 5) return;
    let cancelled = false;
    (async () => {
      // 1. Zipcode lookup → city/state.
      if (lastFetchedZipcodeApiRef.current !== zip) {
        lastFetchedZipcodeApiRef.current = zip;
        try {
          const data = await getZipcodeData(zip);
          if (cancelled) return;
          const city = data?.city != null ? String(data.city).trim() : "";
          const st = (data?.st ?? data?.state ?? "").toString().trim().slice(0, 2).toUpperCase();
          if (city) signupStore.regCity = city;
          if (st) signupStore.regState = st;
        } catch (_) {
          if (lastFetchedZipcodeApiRef.current === zip) lastFetchedZipcodeApiRef.current = null;
        }
      }
      if (cancelled) return;

      // 2. ILF/DLF lookup → coverage defaults + premium.
      if (lastFetchedIlfDlfApiRef.current !== zip) {
        lastFetchedIlfDlfApiRef.current = zip;
        try {
          await fetchIlfDlf({
            zipcode: zip,
            specialtiesid: specialityId,
            ...(effDate ? { effectiveDate: effDate } : {}),
          });
        } catch (_) {
          if (lastFetchedIlfDlfApiRef.current === zip) lastFetchedIlfDlfApiRef.current = null;
        }
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [zip]);

  // ── Google sign-up: prefill name/email from the Google profile only. ──
  // We do NOT attempt sign-in here — clicking the button on /register is a
  // pure prefill action so the user can finish creating the account.
  const handleGoogleSignup = async () => {
    if (googleSignupLoading) return;
    signupStore.googleSignupError = null;
    signupStore.googleSignupLoading = true;
    try {
      const cfg = await fetchGoogleClientId();
      const clientId = cfg?.clientId;
      if (!clientId) throw new Error("Google sign-in is not configured.");
      await loadGsiScript();
      if (!window.google?.accounts?.id) {
        throw new Error("Google was blocked by your browser. Disable ad-blockers and try again.");
      }

      const credential = await new Promise((resolve, reject) => {
        let done = false;
        const timer = setTimeout(() => {
          if (done) return;
          done = true; reject(new Error("Google sign-in timed out"));
        }, 45_000);
        try {
          window.google.accounts.id.initialize({
            client_id: clientId,
            callback: (resp) => {
              const cred = resp?.credential;
              if (done || !cred) return;
              done = true; clearTimeout(timer); resolve(cred);
            },
          });
          window.google.accounts.id.prompt(() => {});
        } catch (e) {
          if (!done) { done = true; clearTimeout(timer); reject(e); }
        }
      });

      const profile = await postSignInWithGoogleData(credential);
      const gd = googleDataFromResponse(profile);
      if (gd.firstname) signupStore.regFirstName = gd.firstname;
      if (gd.lastname) signupStore.regLastName = gd.lastname;
      if (gd.email) signupStore.regEmail = gd.email;
      signupStore.needsSignupCompletion = true;
    } catch (e) {
      signupStore.googleSignupError = toError(e).message;
    } finally {
      signupStore.googleSignupLoading = false;
    }
  };

  // ── Create account: signup + submission in one POST ───────────────────
  const handleCreateAccount = async () => {
    if (signupLoading || !canAdvance()) return;

    signupStore.signupError = null;
    signupStore.signupLoading = true;
    try {
      if (!ilfDlfHasRequiredDefaults(ilfDlfResponse)) {
        throw new Error(ZIPCODE_NOT_AVAILABLE_MSG);
      }

      const submissionReq = buildSubmissionRequest({
        zip, effDate, ilfDlfResponse,
        visitedQuestionGroups, questionGroupsWithIds,
        questionAnswers, impactAnswers,
        hiddenQuestionIds: masterHiddenQuestionIds,
      });

      // Trim every string-typed field so iOS auto-fill / paste-with-trailing
      // -newline doesn't trip the server's exact-match validators.
      const signupPayload = {
        isEntity: false,
        firstName: regFirstName.trim(),
        lastName: regLastName.trim(),
        zipCode: zip.trim(),
        address1: regStreet.trim(),
        address2: "",
        city: regCity.trim() || ilfDlfResponse?.city || "",
        state: (regState.trim() || ilfDlfResponse?.st || ilfDlfResponse?.state || "")
          .slice(0, 2).toUpperCase(),
        email: regEmail.trim(),
        contactNumber: regPhone.trim(),
        password: regPassword,
        title: "Primary location",
        submissionRequest: submissionReq,
      };

      const resp = await postInsuredSignup(signupPayload);

      questionsStore.visitedQuestionGroups = [];
      signupStore.registered = true;
      signupStore.signupDone = true;
      signupStore.needsSignupCompletion = false;
      const sid = submissionIdFromSignupResponse(resp);
      if (sid) submissionStore.flowSubmissionId = sid;
      if (!insuredProfileStore.insuredProfile) {
        insuredProfileStore.insuredProfile = {
          id: resp?.id,
          email: regEmail,
          username: regEmail,
          abbreviation: resp?.abbreviation,
        };
      }
      // Sync reg name into personal/license fields (carries over to License page).
      personalStore.firstName = regFirstName;
      personalStore.lastName = regLastName;
      submissionStore.step = 3;
      navigate(STEP_PATHS[3]);
    } catch (e) {
      signupStore.signupError = toError(e).message.replace(/^Error:\s*/i, "");
    } finally {
      signupStore.signupLoading = false;
    }
  };

  // ── Bounce already-authed users away from /register ───────────────────
  if (isAuthenticated && !needsSignupCompletion) {
    return <Navigate to="/license" replace />;
  }

  // ── Render ─────────────────────────────────────────────────────────────
  return (
    <>
      {/* Estimated-quote strip (shared QuoteStrip). Tapping it opens the
          itemized breakdown overlay — only once an estimate has loaded;
          before that it shows a pending state and stays inert. */}
      <QuoteStrip
        amountLabel="Your estimated quote"
        pendingLabel="Pending…"
        total={rawTotal}
        limits={snapshotLimits}
        claims={snapshotPolicyLine}
        amountPending={!hasEstimate}
        style={{ margin: "10px 0 12px" }}
      />

      <div style={{ textAlign: "center", margin: "10px 0 4px" }}>
        <div style={{ width: 44, height: 44, borderRadius: "50%", background: BRAND_LIGHT, display: "inline-flex", alignItems: "center", justifyContent: "center" }}><UserIcon /></div>
      </div>
      <h2 style={{ fontFamily: "'Fraunces', serif", fontSize: 20, fontWeight: 600, color: "#1a1a1a", textAlign: "center", margin: "8px 0 4px" }}>Create your account</h2>
      <p style={{ fontSize: 13, color: "#777", textAlign: "center", lineHeight: 1.5, margin: "0 0 18px" }}>Your progress is saved automatically. You can return anytime to finish.</p>

      {!needsSignupCompletion && (
        <>
          <div style={{ display: "flex", gap: 8, marginBottom: 0 }}>
            <button onClick={handleGoogleSignup} style={{
              flex: 1, padding: "12px 0", border: "1px solid #d0d0d0", borderRadius: 10,
              background: "#fff", cursor: "pointer", display: "flex", alignItems: "center",
              justifyContent: "center", gap: 8, fontFamily: "'DM Sans', sans-serif",
              fontSize: 13, fontWeight: 500, color: "#333", transition: "background 0.15s",
            }}
              onMouseEnter={(e) => e.currentTarget.style.background = "#f9f9f7"}
              onMouseLeave={(e) => e.currentTarget.style.background = "#fff"}
            >
              <svg width="16" height="16" viewBox="0 0 24 24">
                <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4"/>
                <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
                <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18A11.96 11.96 0 0 0 0 12c0 1.94.46 3.77 1.28 5.4l3.56-2.77.01-.54z" fill="#FBBC05"/>
                <path d="M12 4.75c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 1.09 14.97 0 12 0 7.7 0 3.99 2.47 2.18 6.07l3.66 2.84c.87-2.6 3.3-4.16 6.16-4.16z" fill="#EA4335"/>
              </svg>
              {googleSignupLoading ? "Signing in…" : "Continue with Google"}
            </button>
          </div>
          <Alert type="error" message={googleSignupError} className="alert-center" />

          <div style={{ display: "flex", alignItems: "center", gap: 12, margin: "16px 0" }}>
            <div style={{ flex: 1, height: 1, background: "#e8e8e6" }} />
            <span style={{ fontSize: 12, color: "#aaa", fontFamily: "'DM Sans', sans-serif" }}>or sign up with email</span>
            <div style={{ flex: 1, height: 1, background: "#e8e8e6" }} />
          </div>
        </>
      )}

      <div style={{ display: "flex", gap: 8 }}>
        <Field label="First name" style={{ flex: 1 }}>
          <TextInput value={regFirstName} onChange={(v) => { signupStore.regFirstName = v; }} placeholder="Jane" readOnly={needsSignupCompletion} />
        </Field>
        <Field label="Last name" style={{ flex: 1 }}>
          <TextInput value={regLastName} onChange={(v) => { signupStore.regLastName = v; }} placeholder="Doe" readOnly={needsSignupCompletion} />
        </Field>
      </div>
      <Field label="Email address">
        <TextInput value={regEmail} onChange={(v) => { signupStore.regEmail = v; }} placeholder="jane@example.com" type="email" maxLength={128} readOnly={needsSignupCompletion} />
      </Field>
      <Field label="Create password">
        <div style={{ position: "relative" }}>
          <TextInput
            value={regPassword}
            onChange={(v) => { signupStore.regPassword = v; }}
            placeholder="Min 6 chars, mix of upper/lower/digit/@$!%*?&"
            type={showPw ? "text" : "password"}
            style={{ paddingRight: 40 }}
            maxLength={128}
          />
          <button onClick={() => setShowPw(!showPw)} style={{ position: "absolute", right: 12, top: "50%", transform: "translateY(-50%)", background: "none", border: "none", cursor: "pointer", color: "#999", padding: 0, display: "flex" }}><EyeIcon open={showPw} /></button>
        </div>
        {regPassword && !isValidPassword(regPassword) && (
          <div style={{ fontSize: 11, color: "#a44", marginTop: 4, lineHeight: 1.4 }}>
            {PASSWORD_RULE}
          </div>
        )}
      </Field>
      <Field label="Phone number">
        <TextInput
          value={regPhone}
          onChange={(v) => { signupStore.regPhone = v.replace(/\D/g, "").slice(0, 15); }}
          placeholder="5551234567"
          type="tel"
          inputMode="numeric"
          maxLength={15}
        />
      </Field>

      <Field label="Street address">
        <TextInput value={regStreet} onChange={(v) => { signupStore.regStreet = v; }} placeholder="123 Main St, Apt 4B" />
      </Field>
      <div style={{ display: "flex", gap: 8 }}>
        <Field label="City" style={{ flex: 2 }}>
          <TextInput value={regCity} onChange={(v) => { signupStore.regCity = v; }} placeholder="Los Angeles" />
        </Field>
        <Field label="State" style={{ flex: 1 }}>
          <TextInput value={regState} onChange={(v) => { signupStore.regState = v; }} placeholder="CA" maxLength={2} style={{ textTransform: "uppercase" }} />
        </Field>
        <Field label="ZIP" style={{ flex: 1 }}>
          <TextInput
            value={zip}
            onChange={(v) => { quoteFormStore.zip = v.replace(/\D/g, "").slice(0, 5); }}
            inputMode="numeric"
            maxLength={5}
          />
        </Field>
      </div>

      <div style={{ display: "flex", alignItems: "center", gap: 6, marginTop: 10, marginBottom: 4 }}>
        <SaveIcon />
        <span style={{ fontSize: 11, color: BRAND_DARK }}>Your application auto-saves as you go</span>
      </div>

      <p style={{ fontSize: 11, color: "#aaa", textAlign: "center", lineHeight: 1.5, margin: "6px 0 0" }}>
        By creating an account, you agree to DPL RRG's{" "}
        <span onClick={() => { modalStore.showTerms = true; }} style={{ color: BRAND, textDecoration: "underline", cursor: "pointer" }}>terms of service</span> and{" "}
        <span onClick={() => { modalStore.showPrivacy = true; }} style={{ color: BRAND, textDecoration: "underline", cursor: "pointer" }}>privacy policy</span>.
      </p>

      <Spacer />
      <Alert type="error" message={signupError} className="alert-center" />

      <button
        type="button"
        style={dis(btnPrimary, canAdvance() && !signupLoading)}
        onClick={handleCreateAccount}
      >
        {signupLoading ? "Creating account…" : "Create account & start application"}
      </button>
    </>
  );
}

